import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { pathFor } from '../routes/paths';
import { useLanguage } from '../contexts/LanguageContext';
 

const ACCENT = '#67313a';

const SectionContainer = styled.section`
  position: relative;
  padding: 120px 0;
  background: url('/images/MArbre Blanc - 1500 px.webp') center center/cover no-repeat;
`;

/* Removed parallax overlay; background sits on the section */

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 160px;
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 1060px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 0 40px; /* match global mobile side padding */
    justify-items: center; /* center children horizontally on mobile */
  }
`;

const TextContent = styled(motion.div)`
  color: #3a3a3a;
  justify-self: start;
  text-align: left;
  transform: none;
  max-width: 340px;

  @media (max-width: 1060px) {
    justify-self: center;
    transform: none;
  }
`;

const Title = styled.h2`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(28px, 4.2vw, 40px);
  font-weight: 400;
  text-transform: uppercase;
  color: ${ACCENT};
  margin-bottom: 24px;
  line-height: 1.25;
`;

/* Subtitle not used in the mockup */

const Description = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.9;
  color: #6a6a6a;
  margin-bottom: 22px;
`;

/* Removed stats to match the mockup */

const ImageSection = styled(motion.div)`
  position: relative;
  justify-self: start;
  transform: none;

  @media (max-width: 1060px) {
    justify-self: center;
    transform: none;
    width: 100%;
  }
`;

const PortraitBox = styled.div`
  width: 100%;
  max-width: 420px;
  height: 620px;
  overflow: hidden;
  background: #eee;
  box-shadow: 0 20px 40px rgba(0,0,0,0.12);
  @media (max-width: 1060px) {
    margin: 0 auto; /* ensure equal left/right margins on mobile */
  }
`;

const Portrait = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Credit = styled.div`
  margin-top: 10px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  color: #b0b0b0;
  text-align: right;
`;

const Button = styled(motion.button)`
  background: ${ACCENT};
  border: none;
  padding: 14px 35px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 26px;

  &:hover {
    background: #4e1f28;
    transform: translateY(-2px);
  }
`;

const LightCrafterSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });
  const navigate = useNavigate();
  const { lang } = useLanguage();

  return (
    <SectionContainer ref={ref}>
      <ContentWrapper>
        <TextContent
          initial={{ opacity: 0, x: -40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Title>{lang === 'fr' ? 'Lino Ludovic,\nSculpteur de lumière' : 'Lino Ludovic,\nLight Crafter'}</Title>
          <Description>
            {lang === 'fr'
              ? "Basé entre Lyon et la Provence, je photographie des mariages partout en France et en Europe, de Paris à la Toscane en passant par les îles grecques."
              : 'Based in Lyon and Provence, I capture weddings throughout France and across Europe, from Paris to Tuscany and the Greek Islands.'}
          </Description>
          <Description>
            {lang === 'fr'
              ? "Mon approche mêle lumière naturelle, élégance éditoriale et narration authentique. Je mets l’accent sur les émotions sincères et les détails délicats pour créer des photographies intemporelles, à la fois raffinées et profondément personnelles."
              : 'My approach blends natural light, editorial elegance, and authentic storytelling. I focus on true emotions and delicate details, creating timeless photographs that feel both refined and deeply personal.'}
          </Description>
          <Button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate(pathFor('about', lang))}
          >
            {lang === 'fr' ? "Rencontrer l'artiste" : 'Meet the artist'}
          </Button>
        </TextContent>
        <ImageSection
          initial={{ opacity: 0, x: 40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <PortraitBox>
            <Portrait src="/images/Ludovic Lino - Destination Wedding photographer.webp" alt="Lino Ludovic with couple" />
          </PortraitBox>
        </ImageSection>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default LightCrafterSection;
